/**
 * Platform Marketplace Listings (Super-Admin overview).
 *
 * Read-only summary table for all listings across tenants. The only safe
 * action is "Reindex all" which is idempotent and only updates the search
 * index document (no listing data modified).
 *
 * Route: /platform/marketplace
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  ShieldCheck, Globe, RefreshCw, Eye, EyeOff, Lock, BadgeCheck, Hash,
} from "lucide-react";

const StatusPill = ({ on, archived }) => {
  if (archived) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-amber-50 text-amber-700 border-amber-200">
        <EyeOff size={11} /> archived
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${on ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-50 text-stone-600 border-stone-200"}`}>
      {on ? <Eye size={11} /> : <EyeOff size={11} />}
      {on ? "live" : "hidden"}
    </span>
  );
};

export default function PlatformMarketplace() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [identityStatus, setIdentityStatus] = useState(null);

  const isAdmin = user && user.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [r, idr] = await Promise.all([
        api.get("/platform/marketplace/listings"),
        api.get("/identity/status"),
      ]);
      setItems(r.data.items || []);
      setIdentityStatus(idr.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const reindex = async () => {
    if (!confirm("سيُعاد بناء search index لكل listings. هذه عملية idempotent ولا تعدّل أي listing data. متابعة؟")) return;
    setBusy(true);
    try {
      const r = await api.post("/platform/marketplace/reindex");
      toast.success(`تم إعادة الفهرسة (${r.data.reindexed})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  const runIdentityMigration = async () => {
    if (!confirm("سيُشغّل identity migration (idempotent — لا يكتب فوق أي بيانات موجودة). متابعة؟")) return;
    setBusy(true);
    try {
      const r = await api.post("/identity/migrate/run", null, { headers: { "X-Confirm-Action": "true" } });
      toast.success(`Identity migration: +${r.data.global_users_created} gu, +${r.data.profiles_created} prof`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  if (authLoading) return <div className="p-8 text-center text-stone-500">جاري التحميل…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center max-w-md" data-testid="platform-marketplace-forbidden">
        <Lock className="mx-auto text-rose-500 mb-3" size={36} />
        <h2 className="text-xl font-bold text-stone-800">Super-Admin only</h2>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="platform-marketplace-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-stone-500">
            <ShieldCheck size={14} className="text-[#7A1F3D]" /> Platform · Marketplace
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mt-1">Marketplace Listings (Super-Admin)</h1>
          <p className="text-sm text-stone-500 mt-1">قراءة + عمليتان آمنتان فقط: إعادة فهرسة وهجرة الهوية.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="platform-reindex" disabled={busy} onClick={reindex} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 hover:border-[#7A1F3D] text-sm bg-white">
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Reindex all
          </button>
          <button data-testid="platform-identity-migrate" disabled={busy} onClick={runIdentityMigration} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 hover:border-[#7A1F3D] text-sm bg-white">
            <BadgeCheck size={14} /> Identity migration
          </button>
        </div>
      </div>

      {/* Identity status */}
      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Global users" value={identityStatus?.global_users ?? "—"} icon={Globe} />
        <Stat label="Customer profiles" value={identityStatus?.customer_profiles ?? "—"} icon={Hash} />
        <Stat label="Legacy customers" value={identityStatus?.legacy_customers ?? "—"} icon={Hash} />
        <Stat label="Migration complete" value={identityStatus?.migration_complete ? "Yes" : "Pending"} icon={BadgeCheck} tone={identityStatus?.migration_complete ? "emerald" : "amber"} />
      </div>

      {/* Listings table */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 bg-stone-50/50 font-semibold text-stone-700">
          Listings ({items.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-stone-500 bg-stone-50">
                <th className="text-start px-4 py-2 font-medium">Listing</th>
                <th className="text-start px-4 py-2 font-medium">Tenant</th>
                <th className="text-start px-4 py-2 font-medium">Vertical</th>
                <th className="text-end px-4 py-2 font-medium">Branches</th>
                <th className="text-end px-4 py-2 font-medium">Price</th>
                <th className="text-end px-4 py-2 font-medium">Rating</th>
                <th className="px-4 py-2 font-medium">Visibility</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-center text-stone-500">جاري التحميل…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-stone-500">لا توجد listings.</td></tr>}
              {items.map((l) => (
                <tr key={l.listing_id} className="border-t border-stone-100" data-testid={`listing-row-${l.listing_id}`}>
                  <td className="px-4 py-2">
                    <div className="font-semibold text-stone-800">{l.name?.ar || l.name?.en || l.slug}</div>
                    <div className="text-[11px] text-stone-500 font-mono">{l.slug}</div>
                  </td>
                  <td className="px-4 py-2 text-stone-600 font-mono text-[11px]">{l.tenant_id}</td>
                  <td className="px-4 py-2 text-stone-600">{l.vertical}</td>
                  <td className="px-4 py-2 text-end">{(l.branches || []).length}</td>
                  <td className="px-4 py-2 text-end">{(l.price_range?.min ?? 0)} – {(l.price_range?.max ?? 0)}</td>
                  <td className="px-4 py-2 text-end">{l.rating?.avg ?? 0}/5 ({l.rating?.count ?? 0})</td>
                  <td className="px-4 py-2"><StatusPill on={!!l.visibility?.in_marketplace} archived={l.status === "archived"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, tone = "stone" }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-start gap-3">
    {Icon && <div className={`p-2 rounded-lg bg-${tone}-50 text-${tone}-700`}><Icon size={18} /></div>}
    <div>
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="text-xl font-bold text-stone-800 mt-0.5">{value}</div>
    </div>
  </div>
);
